import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { RobotsFileToken } from '@sleed/bizcommerce-api';
import { useRobotsFile, useSaveRobotsFile } from '~/hooks/use-robots-file';
import { useHasAccess } from '~/lib/auth/use-has-access';
import { robotsFileSchema, tokenToForm, type RobotsFileFormValues } from '~/lib/schemas/robots-file';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { EditorSkeleton } from '~/components/editor-skeleton';
import { RobotsFileEditor } from '~/components/system/robots-file/editor';
import { PageContainer } from '~/components/page-container';

function EditorForm({ token }: { token: RobotsFileToken }) {
	const { t } = useTranslation();
	const save = useSaveRobotsFile();
	const maxContentBytes = token.maxContentBytes ?? 0;
	const schema = useMemo(() => robotsFileSchema(maxContentBytes), [maxContentBytes]);

	const form = useForm<RobotsFileFormValues>({
		resolver: zodResolver(schema) as Resolver<RobotsFileFormValues>,
		defaultValues: tokenToForm(token),
		mode: 'onChange'
	});

	const onSubmit = form.handleSubmit(async values => {
		try {
			// onSuccess updates the query cache, which remounts this form (keyed by lastModifiedUtc) with the saved content
			await save.mutateAsync(values);
			toast.success(t('system.robotsFile.saved'));
		} catch (err) {
			if (!applyServerValidation(err, form.setError)) toast.error(serverMessage(err));
		}
	});

	return (
		<FormProvider {...form}>
			<form onSubmit={onSubmit} noValidate>
				<RobotsFileEditor
					isManaged={token.isManaged ?? false}
					lastModifiedUtc={token.lastModifiedUtc as string | null | undefined}
					maxContentBytes={maxContentBytes}
					saving={save.isPending}
					onCancel={() => form.reset(tokenToForm(token))}
				/>
			</form>
		</FormProvider>
	);
}

export function RobotsFile() {
	const { t } = useTranslation();
	// AuthGuard only checks login; the menu hides this page, but a direct URL must be gated too (server enforces System as well)
	const canManage = useHasAccess()('System');
	const { data, isLoading, isError } = useRobotsFile(canManage);

	return (
		<PageContainer size="wide">
			{!canManage ? (
				<Notice text={t('system.robotsFile.noAccess')} />
			) : isLoading ? (
				<EditorSkeleton />
			) : isError || !data ? (
				<Notice text={t('system.robotsFile.loadFailed')} />
			) : (
				// Remount after save so the unmanaged → managed transition re-seeds the form
				<EditorForm key={String(data.lastModifiedUtc ?? 'unmanaged')} token={data} />
			)}
		</PageContainer>
	);
}

function Notice({ text }: { text: string }) {
	return <div className="rounded-lg border border-border bg-surface p-8 text-center text-sm text-foreground-muted">{text}</div>;
}

export default RobotsFile;
